// src/features/portfolio-optimization-tools/my-strategies/MyStrategiesPage.tsx
//
// "My Strategies" master hub — the saved-portfolios directory + efficient-
// frontier explorer. Reads the paginated header/KPI list from GET /reports/
// (via the multi-asset-optimizer api client), plots every saved strategy on a
// Risk (Max Drawdown) vs. Reward (Net Sharpe) scatter so the user can visually
// spot the mathematically superior book (top-left), and exposes rename-free
// management: toggle the nightly live-tracking daemon and delete.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Scatter,
    ScatterChart,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { Layout, Sidebar } from '../../../shared/ui';
import {
    deleteSavedStrategy,
    disableRoboAdvisor,
    enableRoboAdvisor,
    fetchSavedStrategies,
    updateStrategyTracking,
    type SavedReportListItem,
} from '../multi-asset-portfolio-optimizer/api';
import {
    ActivityIcon,
    DatabaseIcon,
    LoaderIcon,
    SearchIcon,
    ShieldAlertIcon,
    TrashIcon,
} from '../multi-asset-portfolio-optimizer/components/icons';

// Datum shape backing the efficient-frontier scatter plot.
interface ScatterDatum {
    id: string;
    name: string;
    /** X axis: Risk (absolute max drawdown, a fraction). */
    x: number;
    /** Y axis: Reward (net Sharpe ratio). */
    y: number;
    profile: string;
    isTracking: boolean;
}

interface ScatterTooltipProps {
    active?: boolean;
    payload?: { payload: ScatterDatum }[];
}

const ScatterTooltip = ({ active, payload }: ScatterTooltipProps) => {
    if (!active || !payload?.length) return null;
    const data = payload[0].payload;
    return (
        <div className="bg-slate-900 border border-slate-700 p-3 rounded-lg shadow-xl z-50">
            <p className="text-white font-bold mb-1">{data.name}</p>
            <p className="text-emerald-400 text-xs font-mono font-bold">
                Sharpe: {data.y.toFixed(2)}
            </p>
            <p className="text-red-400 text-xs font-mono font-bold">
                Max Drawdown: {(data.x * 100).toFixed(2)}%
            </p>
            <p className="text-slate-500 text-[10px] mt-1.5 uppercase tracking-wider font-semibold">
                {data.profile}
            </p>
        </div>
    );
};

export const MyStrategiesPage = () => {
    const navigate = useNavigate();
    const [strategies, setStrategies] = useState<SavedReportListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [total, setTotal] = useState(0);
    const [loadError, setLoadError] = useState<string | null>(null);

    const loadStrategies = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const res = await fetchSavedStrategies(
                50,
                0,
                'created_at',
                'desc',
                searchQuery,
            );
            setStrategies(res.items);
            setTotal(res.total);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to load strategies:', error);
            setLoadError('Unable to load your saved strategies. Please retry.');
        } finally {
            setLoading(false);
        }
    }, [searchQuery]);

    // Debounce the search input so we don't hammer the API on every keystroke.
    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            void loadStrategies();
        }, 300);
        return () => window.clearTimeout(timeoutId);
    }, [loadStrategies]);

    const handleToggleTracking = async (
        e: MouseEvent,
        id: string,
        currentStatus: boolean,
    ) => {
        e.stopPropagation();
        // Optimistic UI update for instant feedback.
        setStrategies((prev) =>
            prev.map((s) =>
                s.id === id
                    ? { ...s, is_active_for_live_tracking: !currentStatus }
                    : s,
            ),
        );
        try {
            await updateStrategyTracking(id, !currentStatus);
        } catch (error) {
            // Revert on failure.
            // eslint-disable-next-line no-console
            console.error('Failed to toggle live tracking:', error);
            setStrategies((prev) =>
                prev.map((s) =>
                    s.id === id
                        ? { ...s, is_active_for_live_tracking: currentStatus }
                        : s,
                ),
            );
            window.alert('Failed to toggle live tracking status.');
        }
    };

    const handleToggleRobo = async (
        e: MouseEvent,
        id: string,
        currentStatus: boolean,
    ) => {
        e.stopPropagation();
        // Optimistic UI update for instant feedback.
        setStrategies((prev) =>
            prev.map((s) =>
                s.id === id ? { ...s, is_auto_managed: !currentStatus } : s,
            ),
        );
        try {
            if (currentStatus) {
                await disableRoboAdvisor(id);
            } else {
                await enableRoboAdvisor(id);
            }
        } catch (error) {
            // Revert on failure.
            setStrategies((prev) =>
                prev.map((s) =>
                    s.id === id
                        ? { ...s, is_auto_managed: currentStatus }
                        : s,
                ),
            );
            if (error instanceof Error && error.message === 'LIMIT_REACHED') {
                window.alert(
                    'INSTITUTIONAL LIMIT REACHED (409 Conflict):\n'
                        + 'You can only have a maximum of 15 Auto-Managed '
                        + 'active strategies concurrently.',
                );
            } else {
                window.alert('Failed to toggle Robo-Advisor status.');
            }
        }
    };

    const handleDelete = async (e: MouseEvent, id: string, name: string) => {
        e.stopPropagation();
        if (
            !window.confirm(
                `Are you sure you want to permanently delete "${name}"? `
                    + 'All track record history will be lost.',
            )
        ) {
            return;
        }
        try {
            await deleteSavedStrategy(id);
            setStrategies((prev) => prev.filter((s) => s.id !== id));
            setTotal((prev) => Math.max(0, prev - 1));
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to delete strategy:', error);
            window.alert('Failed to delete strategy.');
        }
    };

    // Prepare data for the Efficient Frontier scatter plot.
    const scatterData = useMemo<ScatterDatum[]>(
        () =>
            strategies.map((s) => ({
                id: s.id,
                name: s.name,
                x: Math.abs(s.max_drawdown), // Risk (drawdown).
                y: s.net_sharpe, // Reward (Sharpe).
                profile: s.risk_profile,
                isTracking: s.is_active_for_live_tracking,
            })),
        [strategies],
    );

    const activeDaemons = strategies.filter(
        (s) => s.is_active_for_live_tracking,
    ).length;
    const avgSharpe =
        strategies.length > 0
            ? strategies.reduce((acc, s) => acc + s.net_sharpe, 0)
                  / strategies.length
            : 0;

    const goToDetail = (id: string) =>
        navigate(`/dashboard/my-strategies/${id}`);

    return (
        <Layout
            sidebar={<Sidebar />}
            title="My Saved Strategies"
            subtitle="Manage your custom allocations, track live forward performance, and analyze the efficient frontier."
        >
            {/* ========================================== */}
            {/* TIER 1: The Efficient Frontier Visual      */}
            {/* ========================================== */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
                <div className="surface-card xl:col-span-2">
                    <div className="mb-4">
                        <h3 className="section-title text-lg flex items-center gap-2">
                            <ActivityIcon className="w-5 h-5 text-indigo-500" />
                            Current Universe of Your Strategies (Comparative Risk vs. Reward)
                        </h3>
                        <p className="muted text-xs mt-1">
                            Top-left indicates mathematically superior
                            risk-adjusted returns (high Sharpe, low drawdown).
                            <span className="text-emerald-500 font-semibold">
                                {' '}
                                Green
                            </span>{' '}
                            dots are actively tracked live.
                        </p>
                    </div>
                    <div className="w-full h-[280px]">
                        {scatterData.length === 0 ? (
                            <div className="h-full flex items-center justify-center muted text-sm">
                                {loading
                                    ? 'Loading strategy universe…'
                                    : 'Save a strategy to plot it on the frontier.'}
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <ScatterChart
                                    margin={{ top: 20, right: 20, bottom: 20, left: 0 }}
                                >
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke="#334155"
                                        opacity={0.3}
                                        vertical={false}
                                    />
                                    <XAxis
                                        type="number"
                                        dataKey="x"
                                        name="Max Drawdown"
                                        tickFormatter={(v: number) =>
                                            `${(v * 100).toFixed(0)}%`
                                        }
                                        stroke="#64748b"
                                        fontSize={11}
                                        reversed
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        type="number"
                                        dataKey="y"
                                        name="Net Sharpe"
                                        stroke="#64748b"
                                        fontSize={11}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <RechartsTooltip
                                        content={<ScatterTooltip />}
                                        cursor={{
                                            strokeDasharray: '3 3',
                                            stroke: '#64748b',
                                        }}
                                    />
                                    <Scatter
                                        data={scatterData}
                                        onClick={(node: { id?: string }) => {
                                            if (node?.id) goToDetail(node.id);
                                        }}
                                        className="cursor-pointer"
                                    >
                                        {scatterData.map((entry) => (
                                            <Cell
                                                key={entry.id}
                                                fill={
                                                    entry.isTracking
                                                        ? '#10b981'
                                                        : '#6366f1'
                                                }
                                                opacity={0.8}
                                            />
                                        ))}
                                    </Scatter>
                                </ScatterChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                <div className="surface-card flex flex-col justify-center gap-8">
                    <div>
                        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 flex items-center gap-1.5">
                            <DatabaseIcon className="w-4 h-4" /> Active Strategies
                        </p>
                        <p className="text-4xl font-black text-emerald-500 dark:text-emerald-400 font-mono">
                            {activeDaemons}{' '}
                            <span className="text-xl text-slate-500 font-sans">
                                / {strategies.length}
                            </span>
                        </p>
                        <p className="text-xs text-slate-500 mt-2">
                            All strategies undergo daily walk-forward optimization. Testing ensures that these strategies remain robust and adaptive.
                        </p>
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 flex items-center gap-1.5">
                            <ShieldAlertIcon className="w-4 h-4" /> Average Net
                            Sharpe Ratio
                        </p>
                        <p className="text-4xl font-black text-indigo-500 dark:text-indigo-400 font-mono">
                            {avgSharpe.toFixed(2)}
                        </p>
                    </div>
                </div>
            </div>

            {/* ========================================== */}
            {/* TIER 2: The Data Grid                      */}
            {/* ========================================== */}
            <div className="surface-card !p-0 overflow-hidden">
                <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <h3 className="section-title mb-0">
                        YourSaved Portfolios
                        <span className="ml-2 text-xs font-bold text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded align-middle">
                            {total}
                        </span>
                    </h3>

                    <div className="flex items-center gap-4 w-full sm:w-auto">
                        <div className="relative w-full sm:w-64">
                            <SearchIcon className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search by name…"
                                className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg py-2 pl-9 pr-3 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <button
                            type="button"
                            onClick={() =>
                                navigate(
                                    '/dashboard/multi-asset-portfolio-optimizer',
                                )
                            }
                            className="text-xs whitespace-nowrap font-bold bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-lg transition-colors shadow-sm"
                        >
                            + New Strategy
                        </button>
                    </div>
                </div>

                {loadError && (
                    <div className="px-6 py-3 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/20 border-b border-red-100 dark:border-red-900/40">
                        {loadError}
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-slate-600 dark:text-slate-300">
                        <thead className="text-[10px] uppercase tracking-wider bg-slate-50 dark:bg-slate-900/50 text-slate-500 dark:text-slate-400 font-bold border-b border-slate-200 dark:border-slate-800">
                            <tr>
                                <th className="px-6 py-4">
                                    Strategy Name &amp; Date
                                </th>
                                <th className="px-6 py-4">Risk Profile</th>
                                <th className="px-6 py-4 text-right">
                                    Net Sharpe
                                </th>
                                <th className="px-6 py-4 text-right">
                                    Net CAGR
                                </th>
                                <th className="px-6 py-4 text-center">
                                    Live Tracking of the Strategy
                                </th>
                                <th className="px-6 py-4 text-center">
                                    Forge Ultra Robo-Advisor
                                </th>
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-800/50">
                            {loading ? (
                                <tr>
                                    <td
                                        colSpan={7}
                                        className="text-center py-16 text-slate-500"
                                    >
                                        <LoaderIcon className="w-6 h-6 animate-spin mx-auto text-indigo-500 mb-2" />
                                        Loading…
                                    </td>
                                </tr>
                            ) : strategies.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={7}
                                        className="text-center py-16 text-slate-500"
                                    >
                                        No saved strategies found.
                                    </td>
                                </tr>
                            ) : (
                                strategies.map((s) => (
                                    <tr
                                        key={s.id}
                                        onClick={() => goToDetail(s.id)}
                                        className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors group cursor-pointer"
                                    >
                                        <td className="px-6 py-4">
                                            <div className="font-bold text-indigo-600 dark:text-indigo-400 group-hover:underline">
                                                {s.name}
                                            </div>
                                            <div className="text-[10px] text-slate-400 mt-0.5">
                                                Created{' '}
                                                {new Date(
                                                    s.created_at,
                                                ).toLocaleDateString(undefined, {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric',
                                                })}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="block text-[11px] font-bold text-slate-700 dark:text-slate-300">
                                                {s.risk_profile}
                                            </span>
                                            <span className="inline-block mt-1 text-[9px] uppercase font-bold tracking-wider bg-slate-100 dark:bg-slate-800 text-slate-500 px-2 py-0.5 rounded">
                                                {s.macro_regime_label}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right font-mono font-bold text-emerald-600 dark:text-emerald-400">
                                            {s.net_sharpe.toFixed(2)}
                                        </td>
                                        <td
                                            className={`px-6 py-4 text-right font-mono font-bold ${
                                                s.net_expected_return_ann >= 0
                                                    ? 'text-emerald-600 dark:text-emerald-400'
                                                    : 'text-red-600 dark:text-red-400'
                                            }`}
                                        >
                                            {s.net_expected_return_ann >= 0 ? '+' : ''}
                                            {(s.net_expected_return_ann * 100).toFixed(2)}%
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <button
                                                type="button"
                                                onClick={(e) =>
                                                    handleToggleTracking(
                                                        e,
                                                        s.id,
                                                        s.is_active_for_live_tracking,
                                                    )
                                                }
                                                className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none ${
                                                    s.is_active_for_live_tracking
                                                        ? 'bg-emerald-500'
                                                        : 'bg-slate-300 dark:bg-slate-600'
                                                }`}
                                                title={
                                                    s.is_active_for_live_tracking
                                                        ? 'Pause live tracking'
                                                        : 'Resume live tracking'
                                                }
                                            >
                                                <span
                                                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                                        s.is_active_for_live_tracking
                                                            ? 'translate-x-6'
                                                            : 'translate-x-1'
                                                    }`}
                                                />
                                            </button>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <button
                                                type="button"
                                                onClick={(e) =>
                                                    handleToggleRobo(
                                                        e,
                                                        s.id,
                                                        Boolean(
                                                            s.is_auto_managed,
                                                        ),
                                                    )
                                                }
                                                className={`relative inline-flex items-center justify-center px-4 py-1.5 rounded text-[10px] font-bold uppercase tracking-wider transition-all ${
                                                    s.is_auto_managed
                                                        ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 shadow-[0_0_15px_rgba(245,158,11,0.2)]'
                                                        : 'bg-slate-200 text-slate-500 hover:bg-slate-300 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
                                                }`}
                                                title={
                                                    s.is_auto_managed
                                                        ? 'Disable Auto-Pilot'
                                                        : 'Enable Auto-Pilot'
                                                }
                                            >
                                                {s.is_auto_managed
                                                    ? 'ACTIVE'
                                                    : 'ENABLE'}
                                            </button>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                type="button"
                                                onClick={(e) =>
                                                    handleDelete(e, s.id, s.name)
                                                }
                                                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded transition-colors opacity-0 group-hover:opacity-100"
                                                title="Delete strategy"
                                            >
                                                <TrashIcon className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </Layout>
    );
};

export default MyStrategiesPage;
